import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { QuickAssessmentResult } from '../../services/smart-matching.service'
import { Package, packagesService } from '../../services/packages.service'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface PackageSelectionStepProps {
  assessmentResult: QuickAssessmentResult
  onNext: (selectedPackage: Package) => void
  onBack: () => void
}

interface PackageCardProps {
  pkg: Package
  isRecommended: boolean
  isSelected: boolean
  onSelect: () => void
}

const PackageCard: React.FC<PackageCardProps> = ({ pkg, isRecommended, isSelected, onSelect }) => {
  const packageColors = {
    BASIC: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      header: 'bg-gray-100 text-gray-800',
      button: 'bg-gray-600 hover:bg-gray-700',
    },
    STANDARD: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      header: 'bg-blue-100 text-blue-800',
      button: 'bg-blue-600 hover:bg-blue-700',
    },
    PREMIUM: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      header: 'bg-green-100 text-green-800',
      button: 'bg-green-600 hover:bg-green-700',
    },
  }

  const colors = packageColors[pkg.type] || packageColors.BASIC

  return (
    <div
      className={`
        relative rounded-lg border-2 p-6 cursor-pointer transition-all
        ${isSelected ? `${colors.border} ${colors.bg} shadow-lg` : 'border-gray-200 hover:border-gray-300'}
      `}
      onClick={onSelect}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-4">
          <Badge variant="default" className="bg-orange-500 text-white px-3 py-1">
            Recommended
          </Badge>
        </div>
      )}

      {isSelected && (
        <div className="absolute top-4 right-4">
          <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}

      <div className={`${colors.header} rounded-lg p-4 mb-4 text-center`}>
        <h3 className="text-lg font-bold">{pkg.name}</h3>
        <div className="text-2xl font-bold mt-2">
          RM {pkg.price.toLocaleString()}
        </div>
      </div>

      <p className="text-neutral-600 mb-4 text-sm">
        {pkg.description}
      </p>

      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-neutral-900 mb-2">What's Included:</h4>
          <ul className="space-y-2">
            {pkg.includes.slice(0, 4).map((item, index) => (
              <li key={index} className="flex items-start text-sm">
                <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {item}
              </li>
            ))}
            {pkg.includes.length > 4 && (
              <li className="text-sm text-neutral-500">
                +{pkg.includes.length - 4} more items
              </li>
            )}
          </ul>
        </div>

        {pkg.excludes.length > 0 && (
          <div>
            <h4 className="font-semibold text-neutral-900 mb-2">Not Included:</h4>
            <ul className="space-y-1">
              {pkg.excludes.slice(0, 2).map((item, index) => (
                <li key={index} className="flex items-start text-sm text-neutral-600">
                  <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
              {pkg.excludes.length > 2 && (
                <li className="text-sm text-neutral-500">
                  +{pkg.excludes.length - 2} more items
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-neutral-600">Service Area:</span>
            <span className="font-medium">
              {pkg.maxDistance ? `Within ${pkg.maxDistance}km` : 'Nationwide'}
            </span>
          </div>
          {pkg.serviceAreas && pkg.serviceAreas.length > 0 && (
            <div className="flex items-start justify-between text-sm">
              <span className="text-neutral-600">Regions:</span>
              <div className="text-right">
                <div className="font-medium text-xs">
                  {pkg.serviceAreas.slice(0, 2).join(', ')}
                  {pkg.serviceAreas.length > 2 && ` +${pkg.serviceAreas.length - 2} more`}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        variant={isSelected ? 'default' : 'outline'}
        className={`w-full mt-6 ${isSelected ? colors.button : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          onSelect()
        }}
      >
        {isSelected ? 'Selected' : 'Select This Package'}
      </Button>
    </div>
  )
}

function PackageSelectionStep({ assessmentResult, onNext, onBack }: PackageSelectionStepProps) {
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [packages, setPackages] = useState<Package[]>([])
  const [packagesLoading, setPackagesLoading] = useState(true)

  // Extract region information from assessment
  const preferredRegion = assessmentResult.assessment.preferredBurialArea
  const urgencyLevel = assessmentResult.assessment.urgencyLevel

  // Fetch region-specific packages on component mount
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setPackagesLoading(true)

        // First try to get packages for the specific region
        let fetchedPackages: Package[] = []

        if (preferredRegion) {
          try {
            fetchedPackages = await packagesService.getPackagesByRegion(preferredRegion)
          } catch (error) {
            console.log('No packages found for region:', preferredRegion)
          }
        }

        // If no region-specific packages found, try to get recommended packages based on urgency
        if (fetchedPackages.length === 0) {
          try {
            fetchedPackages = await packagesService.getRecommendedPackages(urgencyLevel, preferredRegion)
          } catch (error) {
            console.log('No recommended packages found')
          }
        }

        // If still no packages, get all active packages
        if (fetchedPackages.length === 0) {
          try {
            fetchedPackages = await packagesService.getAllPackages(true)
          } catch (error) {
            console.error('Error fetching all packages:', error)
            toast.error('Failed to load packages')
          }
        }

        // Get recommended packages from assessment result (if any)
        const assessmentPackages = assessmentResult.recommendedPackages || []

        // Prioritize assessment packages, then region packages
        const finalPackages = assessmentPackages.length > 0 ? assessmentPackages : fetchedPackages

        setPackages(finalPackages)

        // Show region-specific info if we found region packages
        if (fetchedPackages.length > 0 && preferredRegion) {
          toast.success(`Showing packages available in ${preferredRegion}`)
        }
      } catch (error) {
        console.error('Error fetching packages:', error)
        toast.error('Failed to load packages')
        setPackages([])
      } finally {
        setPackagesLoading(false)
      }
    }

    fetchPackages()
  }, [preferredRegion, urgencyLevel, assessmentResult.recommendedPackages])

  const handlePackageSelect = (pkg: Package) => {
    setSelectedPackage(pkg)
  }

  const handleContinue = async () => {
    if (!selectedPackage) return

    setIsLoading(true)
    try {
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1000))
      onNext(selectedPackage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <span className="text-2xl">📦</span>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900">
          Choose Your Funeral Package
        </h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          Based on your assessment, we've prepared packages that suit your needs.
          All packages follow Islamic funeral guidelines and include essential services.
          {preferredRegion && (
            <span className="block mt-2 text-primary-600 font-medium">
              Showing packages available in <strong>{preferredRegion}</strong>
            </span>
          )}
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-2">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Quick Assessment</span>
        </div>
        <div className="w-8 h-0.5 bg-green-600"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            2
          </div>
          <span className="ml-2 text-sm font-medium text-primary-600">Package Selection</span>
        </div>
        <div className="w-8 h-0.5 bg-neutral-300"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-300 text-neutral-500 rounded-full flex items-center justify-center text-sm font-medium">
            3
          </div>
          <span className="ml-2 text-sm text-neutral-500">Deceased Information</span>
        </div>
        <div className="w-8 h-0.5 bg-neutral-300"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-300 text-neutral-500 rounded-full flex items-center justify-center text-sm font-medium">
            4
          </div>
          <span className="ml-2 text-sm text-neutral-500">Provider Matches</span>
        </div>
      </div>

      {/* Assessment Summary */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Assessment Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-1">🚨</div>
              <div className="font-medium text-neutral-900">
                {assessmentResult.assessment.urgencyLevel}
              </div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-1">📍</div>
              <div className="font-medium text-neutral-900">
                {assessmentResult.assessment.locationType}
              </div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-1">🏥</div>
              <div className="font-medium text-neutral-900">
                {assessmentResult.assessment.currentLocation}
              </div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-1">⛰️</div>
              <div className="font-medium text-neutral-900">
                {assessmentResult.assessment.preferredBurialArea}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Package Options */}
      {packagesLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : packages.length === 0 ? (
        <Card>
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">
              No Packages Available
            </h3>
            <p className="text-neutral-600 mb-4">
              {preferredRegion
                ? `No packages are currently available in ${preferredRegion}. Please contact our support team for assistance.`
                : 'No packages are currently available. Please contact our support team for assistance.'
              }
            </p>
            <div className="text-sm text-neutral-500">
              <p>Email us at support@akhirat.co for assistance.</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg) => {
            const isRecommended = pkg.isPopular || pkg.type === 'STANDARD'
            return (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                isRecommended={isRecommended}
                isSelected={selectedPackage?.id === pkg.id}
                onSelect={() => handlePackageSelect(pkg)}
              />
            )
          })}
        </div>
      )}

      {/* Package Comparison Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Islamic Funeral Compliance
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>All packages include:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Ghusl (ritual washing) according to Islamic practice</li>
                <li>Kafan (shrouding) with appropriate materials</li>
                <li>Janazah prayer coordination</li>
                <li>Respectful handling and timely burial</li>
                <li>Coordination with local mosque authorities</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isLoading || packagesLoading}
        >
          Previous
        </Button>
        <div className="flex-1"></div>
        <Button
          variant="islamic"
          onClick={handleContinue}
          disabled={!selectedPackage || isLoading || packagesLoading}
          loading={isLoading}
          className="min-w-48"
        >
          {isLoading ? 'Preparing Next Steps...' : 'Continue to Deceased Information'}
        </Button>
      </div>
    </div>
  )
}

export default PackageSelectionStep